import {useRef, useState, useEffect, useContext} from 'react';
import heic2any from 'heic2any';
import {useDropzone} from 'react-dropzone';
import {consistentSpacing} from '../../APIForms/variables';
import {useDermaApi} from '../../../api/derma';
import {Box, Stack, Chip, Grid, MenuItem, Select, Typography, useMediaQuery} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {ImagePreview} from './imagePreview';
import APIServiceFormContext from '../../../context/APIServiceFormContext';
import {calculateBinarySizeFromBase64Size, truncateToDecimal} from '../../../utils/miscellaneous';
import {AnimationLoadingScreenWithProgressBar} from '../../loading/loadingScreen';
import {delay} from '../../../utils/miscellaneous';

/** 1.4 mega bytes in bytes (maximum allowed by the dermadetect api). this is the maximum weight for a photo uploaded*/
// const maxPhotoSizeBytes = Math.trunc(calculateBinarySizeFromBase64Size(2) * Math.pow(2, 20));

/** Object with all the body locations provided by dermadetect. the keys are the main ones, and each key has an array with more specific locations within that one */
const bodyLocations = {
  arm: [
    'elbow',
    'elbow pit',
    'fingers',
    'forearm external',
    'forearm internal',
    'hand external',
    'hand internal',
    'nails',
    'upper arm external',
    'upper arm internal',
    'wrist external',
    'wrist internal',
  ],
  back: ['back', 'buttock', 'shoulders'],
  buttock: ['buttock', 'scrotum'],
  chest: ['abdomen', 'armpit', 'chest', 'navel'],
  head: [
    'cheeks',
    'chin',
    'ear',
    'eye',
    'eyebrow',
    'eyelid',
    'forehead',
    'gingiva',
    'lips',
    'neck',
    'nose',
    'perioral',
    'rear neck',
    'scalp',
    'tongue',
  ],
  'groin area': ['groins', 'penis', 'pubes', 'vaginal region'],
  leg: [
    'foot external',
    'foot internal',
    'knee',
    'kneepit',
    'nails',
    'shin external',
    'shin internal',
    'thigh external',
    'thigh internal',
    'toes',
  ],
};

const secondaryLocationLabels = {
  // arm
  elbow: 'elbow',
  'elbow pit': 'crease of the elbow',
  fingers: 'fingers',
  'forearm external': 'lower outer arm',
  'forearm internal': 'forearm internal',
  'hand external': 'hand external',
  'hand internal': 'palm',
  nails: 'nails', // this is part of leg too
  'upper arm external': 'outer bicep',
  'upper arm internal': 'inner bicep',
  'wrist external': 'wrist external',
  'wrist internal': 'inside of the wrist',
  // back
  back: 'back',
  shoulders: 'shoulders',
  buttock: 'butt cheek', // this one is part of buttock and back
  // buttock
  scrotum: 'anus',
  // chest
  abdomen: 'abdominal region',
  armpit: 'armpit',
  chest: 'chest',
  navel: 'belly button',
  // head
  cheeks: 'face',
  chin: 'jawline',
  ear: 'earlobe',
  eye: 'eye',
  eyebrow: 'eyebrow',
  eyelid: 'eyelid',
  forehead: 'forehead',
  gingiva: 'gums',
  lips: 'lips',
  neck: 'neck',
  nose: 'nose',
  perioral: 'around the mouth',
  'rear neck': 'back of the neck',
  scalp: 'top of head',
  tongue: 'tongue',
  // leg
  'foot external': 'foot external',
  'foot internal': 'arch of foot',
  knee: 'knee',
  kneepit: 'crease of the knee',
  'shin external': 'outer lower leg',
  'shin internal': 'inside the lower leg',
  'thigh external': 'outer thigh',
  'thigh internal': 'inner thigh',
  toes: 'toes',
  groins: 'pubic region',
  penis: 'penis',
  pubes: 'pubic hair area',
  'vaginal region': 'vagina',
};

/**
 * INDIVIDUAL questions regarding all the body_location_{[1-3]} questions that dermadetect provides. These props are intenteded to work with the Dermadetect form's system.
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data that's going to be saved and possibly sent, comes from a useState value
 * @param {function} props.setData - setter of props.data useState value
 * @param {Object} props.data - addition data that's going to be saved and not sent, is just to store additional things that are not required by the form but are visible to the user
 * @param {function} props.setData - setter of props.additionalData useState value
 *
 * @returns {React.Component}
 */
export const BodyLocationComponent = ({data, setData, additionalData, setAdditionalData}) => {
  const {setValidationFunc} = useContext(APIServiceFormContext);
  const mobileScreenSize = useMediaQuery('(max-width:500px)');
  const [instantError, setInstantError] = useState(undefined);

  const timeoutRef = useRef(null);
  useEffect(() => {
    clearTimeout(timeoutRef.current);
    setValidationFunc(func => {
      let newFunc = func.bind({}); //way to copy a function
      newFunc.instantError = instantError;
      return newFunc;
    });
    if (instantError === undefined) return;
    timeoutRef.current = setTimeout(() => {
      setInstantError(undefined);
    }, 4000);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [instantError]);

  const [primaryLocation, setPrimaryLocation] = useState(data.primary === undefined ? '' : data.primary);
  const [multiSelectOpened, setMultiSelectOpened] = useState(false);
  const [secondaryLocations, setSecondaryLocations] = useState(data.secondary === undefined ? [] : data.secondary);
  const [primarySelected, setPrimarySelected] = useState(data.primary !== undefined);

  const [imageURLs, setImageURLs] = useState(data.image_urls === undefined ? [] : data.image_urls);
  const [uploadedFiles, setUploadedFiles] = useState(
    additionalData.uploadedFiles === undefined ? [] : additionalData.uploadedFiles,
  );
  const [loading, setLoading] = useState(false);
  const [responseReturned, setResponseReturned] = useState(false);
  const [previousUploadedFilesLength, setPreviousUploadedFilesLength] = useState(
    additionalData.previousUploadedFilesLength === undefined
      ? uploadedFiles.length
      : additionalData.previousUploadedFilesLength,
  );

  const dermaApi = useDermaApi();

  useEffect(() => {
    if (data === undefined || additionalData === undefined) return;
    if (data.primary === undefined || data.secondary === undefined || data.image_urls === undefined) return;
    if (additionalData.uploadedFiles === undefined || additionalData.previousUploadedFilesLength === undefined) return;

    if (primaryLocation === '') {
      setPrimaryLocation(data.primary);
      setPrimarySelected(true);
    }
    if (secondaryLocations.length === 0) {
      setSecondaryLocations(data.secondary);
    }
    if (imageURLs.length === 0) {
      setImageURLs(data.image_urls);
      setUploadedFiles(additionalData.uploadedFiles);
      setPreviousUploadedFilesLength(additionalData.previousUploadedFilesLength);
    }
  }, [additionalData, data]);

  useEffect(() => {
    let newData = {...data};
    newData.secondary = secondaryLocations;
    newData.primary = primaryLocation;
    newData.image_urls = imageURLs;
    setData(newData);
    let newAdditionalData = {...additionalData};
    newAdditionalData.uploadedFiles = uploadedFiles;
    newAdditionalData.previousUploadedFilesLength = previousUploadedFilesLength;
    setAdditionalData(newAdditionalData);
  }, [secondaryLocations, primaryLocation, imageURLs, uploadedFiles, previousUploadedFilesLength]);

  //custom dropzone code
  const onDrop = async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length !== 0) {
      setInstantError('Please only upload valid PNG, HEIC and/or JPEG files.');
      return;
    }
    //creating urls for previewing and assigning those to the actual array of files so URLs can be accessed when iterating. also creating the dermadetect image routes
    //starts validating the image
    setLoading(true);
    let allImagesAreValid = true;
    const filesWithRoute = await Promise.all(
      acceptedFiles.map(async (file, idx) => {
        const url = URL.createObjectURL(file);
        file.url = url;
        let image_valid = true;

        const onLoadPromise = new Promise(resolve => {
          const formData = new FormData();
          formData.append('image_file', file, file.name);
          dermaApi.postImageUrls(formData).then(res => {
            const data = res.data;
            if (data.status === 'aimd-only') {
              setImageURLs(prevData => [...prevData, '']);
              resolve(file); // Resolve the promise when finished
              return;
            }

            dermaApi.postImageValidity(data.image_name).then(res => {
              const is_valid = res.data;
              if (is_valid) {
                setImageURLs(prevData => [...prevData, data.image_name]);
                resolve(file); // Resolve the promise when finished
                return;
              }

              image_valid = false;
              allImagesAreValid = false;
              resolve(file); // Resolve the promise even in case of error
            });
          });
        });

        if (/^image\/(heic|heif)$/.test(file.type)) {
          let fileToBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
          });
          file.url = URL.createObjectURL(fileToBlob);
        }
        await onLoadPromise; // Wait for the reader.onload to finish before proceeding

        return image_valid ? file : undefined;
      }),
    );

    const previousFilesLength = uploadedFiles.length;
    setPreviousUploadedFilesLength(previousFilesLength);
    let newFiles = uploadedFiles.concat(
      filesWithRoute.filter(f => f !== undefined), // so it doesn't consider overweight photos
    );

    setUploadedFiles(newFiles);

    if (!allImagesAreValid) {
      setInstantError(
        'One or more of the images you just uploaded is not valid. Make sure the skin occupies most of the space.',
      );
      setPreviousUploadedFilesLength(newFiles.length);

      setResponseReturned(true);
      delay(2).then(() => {
        setLoading(false);
      });
    }
  };
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg', '.heic'],
      'image/png': [],
    },
  });
  //dropzone styles
  const borderColor = '#C3C2C2';
  const altColor = '#5CA0D2';
  // end of custom dropzone code

  if (secondaryLocations.length === 0 && imageURLs.length !== 0) {
    setImageURLs([]);
    setPreviousUploadedFilesLength(0);
    setUploadedFiles([]);
  }
  // conditional that will ensure the loading state works correctly
  if (uploadedFiles.length !== previousUploadedFilesLength && loading) {
    setPreviousUploadedFilesLength(uploadedFiles.length);

    setResponseReturned(true);
    delay(2).then(() => {
      setLoading(false);
    });
  }
  return (
    <Grid
      container
      justifyContent="top"
      alignItems="center"
      flexDirection="column"
      rowGap={consistentSpacing}
      sx={{
        backgroundColor: '#FBFBFB',
        px: {xs: 0, sm: 2},
        borderRadius: 5,
        flex: 1,
        width: {
          xs: '100%',
          md: '600px',
          lg: '750px',
        },
      }}
    >
      <Grid item xs={12}>
        <Typography
          sx={{
            fontWeight: 500,
            textAlign: {xs: 'left', sm: 'center'},
            lineHeight: {xs: 1.2, sm: 1.5},
          }}
        >
          Where is the skin condition located?
        </Typography>{' '}
      </Grid>

      <Grid item xs={12} sx={{width: '100%'}}>
        <Select
          value={primaryLocation}
          onChange={e => {
            if (primarySelected) {
              setSecondaryLocations([]);
              setPreviousUploadedFilesLength(0);
              setUploadedFiles([]);
              setImageURLs([]);
            }

            setPrimaryLocation(e.target.value);
            setPrimarySelected(true);
          }}
          sx={{
            '& .MuiTypography-root': {whiteSpace: 'normal !important'},
            width: '100%',
            maxWidth: '750px',
          }}
          variant="standard"
          size="small"
        >
          {Object.keys(bodyLocations).map((bodyLocation, idx) => (
            <MenuItem
              key={idx}
              value={bodyLocation}
              sx={{
                whiteSpace: 'normal !important',
                paddingX: 1,
                textAlign: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '.9rem',
                        md: '.95rem',
                      },
                      textAlign: 'center !important',
                    }}
                  >
                    {bodyLocation.charAt(0).toUpperCase() + bodyLocation.slice(1)}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Grid>
      {primaryLocation !== '' && (
        <>
          <Grid item>
            <Typography
              sx={{
                fontWeight: 500,
                textAlign: {xs: 'justify', sm: 'center'},
                lineHeight: {xs: 1.2, sm: 1.5},
              }}
            >
              Select the specific areas where the skin condition is located.
            </Typography>
          </Grid>
          <Grid
            item
            container
            xs={12}
            sx={{
              flex: 1,
            }}
            alignItems="flex-start"
            alignContent="flex-start"
            justifyContent="flex-start"
            flexDirection="column"
          >
            <Select
              sx={{
                '& .MuiTypography-root': {
                  whiteSpace: 'normal !important',
                },
                '& .MuiStack-root': {
                  mb: 1,
                },
                width: '100%',
              }}
              variant="standard"
              size="small"
              multiple
              value={secondaryLocations}
              open={multiSelectOpened}
              onOpen={() => setMultiSelectOpened(true)}
              onClose={() => setMultiSelectOpened(false)}
              onChange={e => {
                if (e.target.value.includes('exit')) {
                  setMultiSelectOpened(false);
                  return;
                }
                setSecondaryLocations(e.target.value);
              }}
              renderValue={selected => {
                return (
                  <Stack gap={1} direction="row" flexWrap="wrap">
                    {selected.map(value => {
                      return (
                        <Chip
                          key={value}
                          label={secondaryLocationLabels[value].replace(/^\w/, c => c.toUpperCase())}
                          onDelete={() =>
                            setSecondaryLocations(secondaryLocations.filter(location => location !== value))
                          }
                          deleteIcon={<CancelIcon onMouseDown={event => event.stopPropagation()} />}
                        />
                      );
                    })}
                  </Stack>
                );
              }}
            >
              {bodyLocations[primaryLocation].map((location, idx) => {
                return (
                  <MenuItem key={idx} value={location} sx={{justifyContent: 'space-between'}}>
                    {secondaryLocationLabels[location].replace(/^\w/, c => c.toUpperCase())}
                    {secondaryLocations.includes(location) ? <CheckIcon sx={{color: '#35C6C9'}} /> : null}
                  </MenuItem>
                );
              })}
              {mobileScreenSize && (
                <MenuItem value="exit" sx={{fontWeight: 'bold'}}>
                  Close this box
                </MenuItem>
              )}
            </Select>
          </Grid>
        </>
      )}
      {secondaryLocations.length !== 0 && (
        <>
          <Grid item>
            <Typography
              textAlign={'center'}
              sx={{
                fontWeight: 500,
                textAlign: {xs: 'justify', sm: 'center'},
                lineHeight: {xs: 1.2, sm: 1.5},
              }}
            >
              Upload two or more photos of the area(s) of concern.
            </Typography>
          </Grid>
          <Grid item container xs={12} mt={2}>
            <Box
              className="dropzone"
              {...getRootProps()}
              sx={{
                borderRadius: 5,
                border: !isDragActive ? `3px solid ${borderColor}` : `6px solid ${altColor}`,
                margin: 'auto',
                backgroundColor: !isDragActive ? '#FBFBFB' : 'rgba(0,0,0,0.1)',
                opacity: 2,
                py: 1.5,
                px: 5,
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
                userSelect: 'none',
                cursor: 'pointer',
              }}
            >
              <input {...getInputProps()} />
              <Typography variant="h6" fontWeight={'bold'} lineHeight={1.2}>
                Drop your images here, or <span style={{color: altColor, display: 'inline'}}>upload</span>.
              </Typography>
              <Typography
                variant="subtitle2"
                mt={0.5}
                sx={{
                  fontWeight: 400,
                  fontSize: {
                    xs: 14,
                    sm: 16,
                  },
                }}
              >
                We support PNG, HEIC and JPEG files.
                {/* We support PNG and JPEG files smaller than 1.4 MB.  */}
              </Typography>
            </Box>
          </Grid>
          {loading && (
            <AnimationLoadingScreenWithProgressBar
              title="Validating the image(s)..."
              secondsToLoad={30}
              isAlmostComplete={responseReturned}
            />
          )}
          {uploadedFiles.length !== 0 && (
            <Grid
              item
              container
              rowGap={2}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              {uploadedFiles.map((file, idx) => (
                <Grid
                  item
                  xs={12}
                  sm={5.5}
                  container
                  key={idx}
                  sx={{
                    border: `1px solid #C3C2C2`,
                    borderRadius: 5,
                    justifyContent: 'space-between',
                    overflow: 'hidden',
                  }}
                >
                  <Grid item xs={3} sx={{alignSelf: 'stretch', overflow: 'hidden'}}>
                    <ImagePreview src={file.url} width={{xs: '100px', sm: '140px'}} height={'50px'} />
                  </Grid>
                  <Grid
                    item
                    xs={5}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{width: 'fit-content !important'}}>
                      {file.name.length > 10
                        ? file.name.substring(0, 4) + '...' + file.name.slice(file.name.lastIndexOf('.') + 1)
                        : file.name}
                    </Typography>
                  </Grid>
                  <Grid
                    xs={1}
                    item
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                    }}
                  >
                    <CloseIcon
                      sx={{
                        cursor: 'pointer',
                        fontSize: {
                          xs: 20,
                        },
                      }}
                      onClick={() => {
                        const newFiles = uploadedFiles.filter(f => f !== file);
                        const newImageURLS = imageURLs.filter((_, index) => index !== idx);
                        setImageURLs(newImageURLS);
                        setUploadedFiles(newFiles);
                        setPreviousUploadedFilesLength(newFiles.length);
                      }}
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
